/**
 * An action that may be unavailable: truly disabled, with the reason in words beside it
 * (Mahmoud's action rule; UX_SPEC 3: "visible reason text adjacent, never title-only").
 *
 * Author: Mahmoud Ragab Ahmed.
 */
import { useId } from "react";

import { Button, type ButtonProps } from "./button";
import { cn } from "@/lib/utils";

export function ActionButton({
  disabledReason = null,
  reasonTestId,
  disabled,
  className,
  ...props
}: ButtonProps & {
  /** When set, the button is disabled and this sentence renders beside it. */
  readonly disabledReason?: string | null;
  readonly reasonTestId?: string;
}): React.JSX.Element {
  const reasonId = useId();
  const blocked = disabledReason !== null;
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-x-3 gap-y-1", className)}>
      <Button
        {...props}
        disabled={disabled === true || blocked}
        aria-disabled={disabled === true || blocked}
        aria-describedby={blocked ? reasonId : undefined}
      />
      {blocked && (
        <span
          id={reasonId}
          className="max-w-[28rem] text-sm text-muted-foreground"
          data-testid={reasonTestId}
        >
          {disabledReason}
        </span>
      )}
    </span>
  );
}
